import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { OutputEc2, Tenancy } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Container, Header, SelectProps, SpaceBetween, Spinner, Table, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useLocation, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import { awsRightSizePolicyIdToString } from '../../../common/RightSizingPolicyName';
import { Query } from '../../../common/table/types';
import { MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../contexts/TcoContext';
import PreviewBanner from '../../common/PreviewBanner';
import { FilterLocationState } from '../../portfolio-data-asset/types';
import { getFilterQuery } from '../../portfolio-data-asset/utils';
import { commonTotalCostSummary, generateCashFlowTable, summaryColumnDefs } from '../common/Common';
import { CommonSummaryItem } from '../common/types';
import { ErrorMsgFormat } from '../error-handler-context/CommonTcoErrorHandler';
import { generateEc2ErrorMsgs } from '../error-handler-context/helper';
import GenerateErrorMessageBox from '../error-handler-context/TcoErrorHandler';
import { getVMWareHostCount } from './AWSHelper';
import {
    buildEC2CostDetailsTabs, buildRIDiscountCashFlow, compareOfferTerm, DedicatedHostDetailTable, EC2CashFlowTable,
    EC2InstanceTypesTable, EC2PaymentDetailTable, EC2PaymentSummaryTable, getVMWareClusterCostItems, paymentTypeTabContent,
    SoftwareAndLicensesCostTable, VMWareClusterCashFlowTable, VMWareClusterCostTable,
} from './EC2CostHelper';

type Props = FlashContextInterface;

const tcoService = new TcoService();

function EC2Cost({ flash }: Props) {
    const { assessment } = useContext(TcoContext);

    const [ loading, setLoading ] = useState(false);
    const [ activeDetailTab, setActiveDetailTab ] = useState('costPerInstance');
    const [ ec2Output, setEC2Output ] = useState<OutputEc2 | null>(null);
    const [ selectedPaymentType, setSelectedPaymentType ] = useState<SelectProps.Option | null>(null);
    const [ errorMessages, setErrorMessages ] = useState<ErrorMsgFormat[]>([]);
    const [ defaultQuery, setDefaultQuery ] = useState<Map<string, Query>>(new Map());
    const { panelHandler } = useContext(HelpPanelContext);

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;
    const isMounted = useIsMounted();
    const history = useHistory();
    const location = useLocation<FilterLocationState>();

    useEffect(() => {
        // if a user navigated here from a link with properties for selecting a tab and applying a default filter to the table
        if (location?.state?.targetValue && location?.state?.targetColumn && location?.state?.targetTab) {
            setActiveDetailTab(location.state.targetTab);
            const query = getFilterQuery(location.state.targetValue, location.state.targetColumn);
            setDefaultQuery((prevState: Map<string, Query>) => {
                prevState.set(location.state.targetTab ?? '', query);
                return prevState;
            });
        }
    }, [ location ]);

    const loadEC2CostData = async () => {
        if (!isMounted()) {
            return;
        }

        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const awsEC2Assessment = await tcoService.getAwsEC2(tcoAssessmentId);
                if (isMounted()) {
                    setEC2Output(awsEC2Assessment.output.aws.ec2);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_EC2);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.EC2_COST);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadEC2CostData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    useEffect(() => {
        if (!ec2Output || !portfolioId) {
            return;
        }
        setErrorMessages(generateEc2ErrorMsgs(ec2Output, portfolioId));
    }, [ ec2Output, portfolioId ]);

    const buildSummaryItems = (output: OutputEc2, rightSizePolicy: string, applyUnusedCapacity: boolean, relocateOn: boolean): CommonSummaryItem[] => {
        const items: CommonSummaryItem[] = [];

        if (output.servers.length) {
            items.push({
                type: constants.EC2_SHARED_AND_DEDICATED_INSTANCES_TYPE,
                description: `${output.servers.length} ${constants.INSTANCE_S} - ${rightSizePolicy}`,
                cost: output.summary.totalEC2Cost,
            });
        }

        if (output.summary.riDiscount) {
            items.push({
                type: constants.VOLUME_DISCOUNT_TYPE,
                description: constants.VOLUME_DISCOUNT_DESCRIPTION,
                cost: output.summary.riDiscount,
            });
        }

        if (output.summary.totalResidual) {
            items.push({
                type: constants.EC2_RESIDUAL_VALUE_TYPE,
                description: constants.RESIDUAL_VALUE_DESCRIPTION,
                cost: output.summary.totalResidual,
            });
        }

        if (output.dedicatedHostRecommendations && output.dedicatedHostRecommendations.length) {
            items.push(
                {
                    type: constants.DEDICATED_HOST_TYPE,
                    description: `${output.dedicatedHostRecommendations.length} ${constants.HOST_S} - ${rightSizePolicy}`,
                    cost: output.summary.totalDedicatedHostCost,
                },
                {
                    type: constants.DEDICATED_HOST_UNUSED_VALUE_TYPE,
                    description: constants.DEDICATED_HOST_UNUSED_VALUE_DESCRIPTION,
                    cost: applyUnusedCapacity ? output.summary.unusedDedicatedHostCost : undefined,
                },
                {
                    type: constants.DEDICATED_HOST_RESIDUAL_VALUE_TYPE,
                    description: constants.DEDICATED_HOST_RESIDUAL_VALUE_DESCRIPTION,
                    cost: output.summary.residualDedicatedHostCost,
                },
            );
        }

        if (relocateOn && output?.vmwareSizeAssessment?.clusters) {
            items.push(
                {
                    type: constants.VMWARE_TYPE,
                    description: `${getVMWareHostCount(output.vmwareSizeAssessment?.clusters ?? [])} ${constants.VMWARE_DESCRIPTION}`,
                    cost: output.summary.totalVmwareCost,
                },
                {
                    type: constants.VMWARE_UNUSED_VALUE_TYPE,
                    description: constants.VMWARE_UNUSED_VALUE_DESCRIPTION,
                    cost: output.summary.unusedVmwareCost,
                },
                {
                    type: constants.VMWARE_RESIDUAL_VALUE_TYPE,
                    description: constants.VMWARE_RESIDUAL_VALUE_DSECRIPTION,
                    cost: output.summary.residualVmwareCost,
                },
            );
        }

        if (output.dedicatedHostRecommendations?.length || output.sharedTenancyOsCostResults) {
            items.push({
                type: constants.THIRD_PARTY_SOFTWARE_LICENSE_TYPE,
                description: constants.SOFTWARE_LICENSE_DESCRIPTION,
                cost: (output.summary.totalDedicatedHostOSCost ?? 0) + (output.summary.totalSharedTenancyOSCost ?? 0),
            });
        }

        return items;
    };

    const getPaymentTypeOptions = (ec2Cost: OutputEc2): SelectProps.Options => {
        if (ec2Cost.pricing && ec2Cost.servers.length > 0) {
            return Object.keys(ec2Cost.servers[0].offers).sort(compareOfferTerm(ec2Cost.pricing)).map(offerCode => {
                return {
                    value: offerCode,
                    label: ec2Cost.pricing.offerDetails[offerCode],
                };
            });
        }

        return [];
    };

    const buildTabs = (ec2Cost: OutputEc2): TabsProps.Tab[] => {
        if (assessment) {
            const dedicatedHostDetailContent = ec2Cost.dedicatedHostRecommendations?.length ?
                DedicatedHostDetailTable(ec2Cost.dedicatedHostRecommendations || [], ec2Cost.currency, ec2Cost.pricing)
                : null;
            const softwareAndLicenseCostContent = (ec2Cost.dedicatedHostRecommendations?.length || ec2Cost.sharedTenancyOsCostResults) ?
                SoftwareAndLicensesCostTable(ec2Cost.currency, ec2Cost.dedicatedHostOsCostResults, ec2Cost.sharedTenancyOsCostResults)
                : null;
            const vmWareCostTabContent = assessment.configuration.tcoAssumptions.relocateOn && ec2Cost?.vmwareCostAssessment?.clusters ? 
                VMWareClusterCostTable(
                    getVMWareClusterCostItems(ec2Cost.vmwareSizeAssessment.clusters, ec2Cost.vmwareCostAssessment.clusters),
                    ec2Cost.currency,
                ) : null;
            const vmWareCashFlowTabContent = assessment.configuration.tcoAssumptions.relocateOn && ec2Cost?.vmwareCostAssessment?.clusters ?
                VMWareClusterCashFlowTable(ec2Cost.vmwareCostAssessment.clusters, ec2Cost.currency, assessment.configuration.tcoAssumptions.durationYears)
                : null;
            return buildEC2CostDetailsTabs(
                EC2InstanceTypesTable(ec2Cost.instanceTypes.filter(instanceType => instanceType.tenancy !== Tenancy.Host), ec2Cost.currency, ec2Cost.pricing),
                EC2CashFlowTable(
                    [...ec2Cost.servers, ...ec2Cost.dedicatedHostServers ?? []],
                    ec2Cost.currency,
                    assessment.configuration.tcoAssumptions.durationYears,
                    ec2Cost.pricing,
                ),
                EC2PaymentDetailTable(
                    [...ec2Cost.servers, ...ec2Cost.dedicatedHostServers ?? []],
                    ec2Cost.currency,
                    ec2Cost.pricing,
                    defaultQuery.get('paymentOptions')
                ),
                generateCashFlowTable(
                    buildRIDiscountCashFlow(ec2Cost.riDiscountCashFlow.yearly),
                    assessment.configuration.tcoAssumptions.durationYears,
                    ec2Cost.currency,
                    constants.EC2_RI_VOLUME_DISCOUNT,
                ),
                EC2PaymentSummaryTable(ec2Cost.pricing.paymentSummary ?? [], ec2Cost.currency, ec2Cost.pricing),
                paymentTypeTabContent(
                    selectedPaymentType,
                    getPaymentTypeOptions(ec2Cost),
                    onPaymentTypeUpdate,
                    ec2Cost.pricing.paymentTypes,
                    ec2Cost.currency,
                ),
                dedicatedHostDetailContent,
                softwareAndLicenseCostContent,
                vmWareCostTabContent,
                vmWareCashFlowTabContent,
            );
        }
        return [];
    };

    const onPaymentTypeUpdate = (option: SelectProps.Option): void => {
        setSelectedPaymentType(option);
    };

    const DHPreviewAlert = (
        <PreviewBanner
            customHeader={constants.DEDICATED_HOST_IN_PREVIEW_HEADER}
            customText={constants.DEDICATED_HOST_IN_PREVIEW}
        />
    );

    /**
     * Called when the user selects a tab
     * keep table query behavior consistent by removing default queries
     * @param activeTabId 
     */
    const onTabSelectionChange = (activeTabId: string): void => {
        setDefaultQuery(new Map());
        setActiveDetailTab(activeTabId);
    };

    const buildServerPageContent = (): JSX.Element => {
        if (loading || !ec2Output || !assessment) {
            return (
                <Spinner size='big' data-testid='spinner'/>
            );
        }

        const currency = ec2Output.currency;
        return (
            <SpaceBetween size='xl' data-testid='pageContent'>
                {(ec2Output.dedicatedHostRecommendations || []).length > 0 || (ec2Output.failedDedicatedHostServers || []).length > 0 ? DHPreviewAlert : null}
                <GenerateErrorMessageBox
                    errorMessages={errorMessages}
                    history={history}
                />
                <Header variant='h1'>{constants.EC2_COST_TITLE}</Header>
                <Table
                    columnDefinitions={summaryColumnDefs(currency)}
                    items={buildSummaryItems(
                        ec2Output,
                        awsRightSizePolicyIdToString(assessment.configuration.awsEC2Pricing.awsRightSizePolicy),
                        assessment.configuration.awsEC2Pricing.applyUnusedCapacity,
                        assessment.configuration.tcoAssumptions.relocateOn,
                    )}
                    sortingDisabled
                />
                {commonTotalCostSummary(currency, ec2Output.summary.totalCost, ec2Output.summary.manualAdjustment.value, ec2Output.summary.adjustedTotalCost)}
                <Header variant='h2'>{constants.DETAILS}</Header>
                <Tabs tabs={buildTabs(ec2Output)} activeTabId={activeDetailTab} onChange={({ detail }) => onTabSelectionChange(detail.activeTabId)} />
            </SpaceBetween>
        );
    };

    return (
        <Container data-testid='container'>
            {buildServerPageContent()}
        </Container>
    );
}

export default withFlashContext(EC2Cost);
